import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Container } from 'react-bootstrap';
import { useTheme } from '../contexts/ThemeContext';
import { StatCard } from '../components/UI';
import { SearchAndFilterBar } from '../components/search-and-filter-bar';
import OverviewHeader from './statistic-page/components/OverviewHeader';
import UsersDonutCard from './statistic-page/components/UsersDonutCard';
import LoadsBarCard from './statistic-page/components/LoadsBarCard';
import EarningsLineCard from './statistic-page/components/EarningsLineCard';
import StatsFilters from '../components/stats/StatsFilters';
import { useStatsFilters } from '../hooks/useStatsFilters';
import { useStatsDataFetch } from '../hooks/useStatsDataFetch';
import { getDateRangeForPeriod } from '../utils/statsFilterUtils';
import { mapKPIData, mapLoadsByStatusDonut, mapMoneyTrendLine, mapPaymentStatusDonut, mapAgingBuckets, mapTopLists } from '../utils/statsDataMapper';
import { mapCarriersData, mapCustomersData } from '../utils/carriersCustomersMapper';
import { exportStatsData } from '../utils/exportUtils';
import './statistic-page/styles.scss';
import './DashboardStats.scss';

const statisticsTabs = [
  { value: 'overview', label: 'Overview' },
  { value: 'loads', label: 'Load' },
  { value: 'payments', label: 'Payment' },
  { value: 'carriers', label: 'Carriers' },
  { value: 'customers', label: 'Customers' }
];

const DashboardStats = () => {
  const { getThemeColors } = useTheme();
  const colors = getThemeColors();
  
  const [selectedTab, setSelectedTab] = useState('overview');
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const userRole = localStorage.getItem('userRole') || 'admin';

  const {
    appliedFilters,
    draftFilters,
    filterSchema,
    updateDraftFilter,
    applyFilters,
    clearFilters,
    syncDraftWithApplied,
    setAppliedFilters
  } = useStatsFilters(selectedTab);

  // Sync draft when tab changes
  useEffect(() => {
    syncDraftWithApplied();
  }, [selectedTab, syncDraftWithApplied]);

  // Update search in draft filters (debounced)
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      updateDraftFilter('search', searchTerm);
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [searchTerm, updateDraftFilter]);

  // Data fetching with deduplication
  const { data: statsData, loading, error, refetch } = useStatsDataFetch(
    selectedTab,
    appliedFilters,
    {
      enabled: true,
      onError: (err) => {
        console.error('[DashboardStats] Fetch error:', err);
      }
    }
  );

  // Prepare KPI cards
  const kpiCards = useMemo(() => {
    if (!statsData) return [];
    return mapKPIData(statsData);
  }, [statsData]);

  // Prepare chart data
  const chartData = useMemo(() => {
    // Default empty data structure
    const defaultChartData = {
      loadsDonut: { title: 'Loads Status', totalLabel: 'Total', total: 0, segments: [] },
      moneyLine: { 
        title: 'Money Trend', 
        subtitle: '', 
        categories: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul'], 
        series: [
          { name: 'Revenue', slug: 'revenue', data: [0, 0, 0, 0, 0, 0, 0] },
          { name: 'Expense', slug: 'expense', data: [0, 0, 0, 0, 0, 0, 0] }
        ]
      },
      paymentDonut: { title: 'Payment Status', totalLabel: 'Total', total: 0, segments: [] },
      agingBar: { title: 'Aging', legend: [], data: [] },
      carriersDonut: { title: 'Carriers', totalLabel: 'Total', total: 0, segments: [] },
      carriersBar: { title: 'Top Carriers', legend: [], data: [] },
      carriersLine: { title: 'Carriers Performance', subtitle: '', categories: [], series: [] },
      customersDonut: { title: 'Customers', totalLabel: 'Total', total: 0, segments: [] },
      customersBar: { title: 'Top Customers', legend: [], data: [] },
      customersLine: { title: 'Customers Performance', subtitle: '', categories: [], series: [] }
    };

    if (!statsData) {
      return defaultChartData;
    }

    try {
      const baseData = {
        loadsDonut: mapLoadsByStatusDonut(statsData),
        moneyLine: mapMoneyTrendLine(statsData),
        paymentDonut: mapPaymentStatusDonut(statsData, 'receivable'),
        agingBar: mapAgingBuckets(statsData, 'receivable')
      };

      // Add carriers/customers data if available
      if (selectedTab === 'carriers' || statsData.carriers) {
        const carriersData = mapCarriersData(statsData);
        baseData.carriersDonut = carriersData.donut;
        baseData.carriersBar = carriersData.bar;
        baseData.carriersLine = carriersData.line;
      }

      if (selectedTab === 'customers' || statsData.customers) {
        const customersData = mapCustomersData(statsData);
        baseData.customersDonut = customersData.donut;
        baseData.customersBar = customersData.bar;
        baseData.customersLine = customersData.line;
      }

      return baseData;
    } catch (error) {
      console.error('[DashboardStats] Error mapping chart data:', error);
      return defaultChartData;
    }
  }, [statsData, selectedTab]);

  // Date label
  const dateLabel = useMemo(() => {
    return new Date().toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  }, []);

  // Handle export
  const handleExport = useCallback(() => {
    if (!statsData) return;
    const periodLabel = appliedFilters.period || 'month';
    exportStatsData(statsData, `stats_${selectedTab}_${periodLabel}`);
  }, [statsData, selectedTab, appliedFilters.period]);

  // Handle filter actions
  const handleApplyFilters = useCallback(() => {
    applyFilters();
    // Refetch will happen automatically via useStatsDataFetch when appliedFilters changes
  }, [applyFilters]);

  const handleClearFilters = useCallback(() => {
    clearFilters();
    setSearchTerm('');
    // Refetch will happen automatically via useStatsDataFetch when appliedFilters changes
  }, [clearFilters]);

  // Handle period change from SearchAndFilterBar (maps to filter period)
  const handlePeriodChange = useCallback((value) => {
    // Map SearchAndFilterBar period values to filter period values
    const periodMap = {
      'today': 'day',
      'this_week': 'week',
      'this_month': 'month',
      'custom': 'custom'
    };
    const period = periodMap[value] || 'month';
    updateDraftFilter('period', period);
    
    // If not custom, apply immediately (no need to open filter modal)
    if (period !== 'custom') {
      updateDraftFilter('startDate', null);
      updateDraftFilter('endDate', null);
      // Apply immediately for quick period changes
      setAppliedFilters(prev => ({ ...prev, period, startDate: null, endDate: null }));
    }
  }, [updateDraftFilter, setAppliedFilters]);

  // Get period value for SearchAndFilterBar
  const periodValueForBar = useMemo(() => {
    const period = appliedFilters.period || 'month';
    const periodMap = {
      'day': 'today',
      'week': 'this_week',
      'month': 'this_month',
      'custom': 'custom'
    };
    return periodMap[period] || 'this_month';
  }, [appliedFilters.period]);

  if (loading && !statsData) {
    return (
      <Container fluid style={{ backgroundColor: colors.background, minHeight: '100vh' }}>
        <div className="statistics-page">
          <div style={{ padding: '20px', textAlign: 'center' }}>
            Loading statistics...
          </div>
        </div>
      </Container>
    );
  }

  if (error && !statsData) {
    return (
      <Container fluid style={{ backgroundColor: colors.background, minHeight: '100vh' }}>
        <div className="statistics-page">
          <div className="statistics-error">
            Error: {error}
          </div>
        </div>
      </Container>
    );
  }

  return (
    <Container fluid style={{ backgroundColor: colors.background, minHeight: '100vh' }}>
      <div className="statistics-page">
        <OverviewHeader
          userName={localStorage.getItem('userName') || 'User'}
          dateLabel={dateLabel}
          onTodayClick={() => {
            updateDraftFilter('period', 'day');
            setAppliedFilters(prev => ({ ...prev, period: 'day', startDate: null, endDate: null }));
          }}
        />

        <div className="stat-cards">
          {kpiCards.map((card, index) => (
            <div className="stat-cards__wrapper" key={index}>
              <StatCard {...card} />
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="statistics-tabs">
          {statisticsTabs.map((tab) => (
            <button
              key={tab.value}
              type="button"
              className={`statistics-tabs__item${
                tab.value === selectedTab ? ' is-active' : ''
              }`}
              onClick={() => setSelectedTab(tab.value)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <SearchAndFilterBar
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          onFilterClick={() => setIsFiltersOpen(true)}
          onExportClick={handleExport}
          searchPlaceholder="Search metrics"
          addButtonLabel="Export"
          filterLabel="Filter By"
          pageType="statistics"
          disableAddModal
          dateRangeOptions={[
            { label: 'Today', value: 'today' },
            { label: 'This Week', value: 'this_week' },
            { label: 'This Month', value: 'this_month' },
            { label: 'Custom Range', value: 'custom' }
          ]}
          dateRangeValue={periodValueForBar}
          onDateRangeChange={handlePeriodChange}
        />

        <div className="statistics-grid">
          {selectedTab === 'overview' && (
            <>
              <UsersDonutCard {...chartData.loadsDonut} />
              <LoadsBarCard
                title="Loads by Status"
                legend={[
                  { label: 'Delivered', slug: 'delivered', key: 'delivered' },
                  { label: 'On Hold', slug: 'onhold', key: 'onhold' }
                ]}
                data={chartData.agingBar.data}
              />
            </>
          )}

          {selectedTab === 'loads' && (
            <>
              <UsersDonutCard {...chartData.loadsDonut} />
              <LoadsBarCard
                title="Loads Performance"
                legend={[
                  { label: 'Delivered', slug: 'delivered', key: 'delivered' },
                  { label: 'Dispatched', slug: 'dispatched', key: 'dispatched' }
                ]}
                data={chartData.agingBar.data}
              />
            </>
          )}

          {selectedTab === 'payments' && (
            <>
              <UsersDonutCard {...chartData.paymentDonut} />
              <LoadsBarCard
                title="Aging Buckets"
                legend={chartData.agingBar.legend}
                data={chartData.agingBar.data}
              />
            </>
          )}

          {selectedTab === 'carriers' && (
            <>
              <UsersDonutCard {...chartData.carriersDonut} />
              <LoadsBarCard
                title={chartData.carriersBar.title}
                legend={chartData.carriersBar.legend}
                data={chartData.carriersBar.data}
              />
            </>
          )}

          {selectedTab === 'customers' && (
            <>
              <UsersDonutCard {...chartData.customersDonut} />
              <LoadsBarCard
                title={chartData.customersBar.title}
                legend={chartData.customersBar.legend}
                data={chartData.customersBar.data}
              />
            </>
          )}
        </div>

        <div className="statistics-bottom">
          {selectedTab === 'carriers' && (
            <EarningsLineCard {...chartData.carriersLine} />
          )}
          {selectedTab === 'customers' && (
            <EarningsLineCard {...chartData.customersLine} />
          )}
          {(selectedTab === 'overview' || selectedTab === 'loads' || selectedTab === 'payments') && (
            <EarningsLineCard {...chartData.moneyLine} />
          )}
        </div>

        <StatsFilters
          isOpen={isFiltersOpen}
          onClose={() => setIsFiltersOpen(false)}
          draftFilters={draftFilters}
          onDraftFilterChange={updateDraftFilter}
          filterSchema={filterSchema}
          userRole={userRole}
          activeTab={selectedTab}
          onApply={handleApplyFilters}
          onClear={handleClearFilters}
        />
      </div>
    </Container>
  );
};

export default DashboardStats;
